import React, { useState, useCallback, useEffect } from 'react';
import { logger } from '@/lib/logger';
import { Annotation } from '@/services/annotationService';
import { useAnnotations, useMeasurementOverride } from '@/hooks/useAnnotations';
import { useFloorPlanScale } from '@/hooks/useFloorPlanScale';
import { wrapTouchEvent } from './touchEventWrapper';
import { getDimensionLabelSizing } from './dimensionLabelSizing';

interface RectangleAnnotationProps {
  annotation: Annotation;
  isSelected: boolean;
  isHovered: boolean;
  scale: number;
  onClick: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onMouseDown: (e: React.MouseEvent) => void;
  onTouchStart?: (e: React.TouchEvent) => void;
  onTouchEnd?: (e: React.TouchEvent) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  isInteractive: boolean;
  floorPlanId?: string;
}

export function RectangleAnnotation({
  annotation,
  isSelected,
  isHovered,
  scale,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
  onTouchStart,
  onTouchEnd,
  onTouchMove,
  isInteractive,
  floorPlanId,
}: RectangleAnnotationProps) {
  const { data, style } = annotation;
  const [isResizing, setIsResizing] = useState(false);
  const [resizePreview, setResizePreview] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [isRotating, setIsRotating] = useState(false);
  const [rotationAngle, setRotationAngle] = useState(data.rotation || 0);
  const { updateAnnotation } = useAnnotations(annotation.floor_plan_id);
  const measurementOverride = useMeasurementOverride();
  const scaleId = floorPlanId || annotation.floor_plan_id || '';
  const { formatMeasurement: fpFormatMeasurement, hasScale: fpHasScale } =
    useFloorPlanScale(scaleId);
  const formatMeasurement = measurementOverride?.formatMeasurement ?? fpFormatMeasurement;
  const hasScale = measurementOverride?.hasScale ?? fpHasScale;
  const dimensionLabel = getDimensionLabelSizing(scale);
  const displayData = resizePreview ? { ...data, ...resizePreview } : data;
  const x = displayData.x!;
  const y = displayData.y!;
  const width = displayData.width!;
  const height = displayData.height!;
  const rotation = data.rotation || 0;

  // Update local rotation angle when annotation changes
  useEffect(() => {
    if (!isRotating) {
      setRotationAngle(rotation);
    }
  }, [rotation, isRotating]);

  // Calculate the stroke width - annotations scale with zoom (no division by scale)
  const strokeWidth = style.strokeWidth || 1;

  // Handle resize start
  const handleResizeStart = useCallback(
    (e: React.MouseEvent, handle: string) => {
      if (!isInteractive) return;
      e.stopPropagation();
      e.preventDefault();
      setIsResizing(true);

      // Get the SVG element once
      const svg =
        (e.target as SVGElement).ownerSVGElement || (e.target as SVGElement).closest('svg');
      if (!svg) return;

      // Get initial mouse position in SVG coordinates
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const initialMousePos = pt.matrixTransform(svg.getScreenCTM()!.inverse());

      // Store the original values from annotation data
      const originalData = {
        x: annotation.data.x!,
        y: annotation.data.y!,
        width: annotation.data.width!,
        height: annotation.data.height!,
      };

      // Track the last updated values
      let lastX = originalData.x;
      let lastY = originalData.y;
      let lastWidth = originalData.width;
      let lastHeight = originalData.height;

      const handleMove = (clientX: number, clientY: number) => {
        const pt = svg.createSVGPoint();
        pt.x = clientX;
        pt.y = clientY;
        const currentMousePos = pt.matrixTransform(svg.getScreenCTM()!.inverse());

        // Calculate delta from initial mouse position
        const deltaX = currentMousePos.x - initialMousePos.x;
        const deltaY = currentMousePos.y - initialMousePos.y;

        let newX = originalData.x;
        let newY = originalData.y;
        let newWidth = originalData.width;
        let newHeight = originalData.height;

        switch (handle) {
          case 'nw': // top-left
            newX = originalData.x + deltaX;
            newY = originalData.y + deltaY;
            newWidth = originalData.width - deltaX;
            newHeight = originalData.height - deltaY;
            // Ensure minimum size
            if (newWidth < 10) {
              newX = originalData.x + originalData.width - 10;
              newWidth = 10;
            }
            if (newHeight < 10) {
              newY = originalData.y + originalData.height - 10;
              newHeight = 10;
            }
            break;
          case 'ne': // top-right
            newY = originalData.y + deltaY;
            newWidth = originalData.width + deltaX;
            newHeight = originalData.height - deltaY;
            // Ensure minimum size
            if (newWidth < 10) newWidth = 10;
            if (newHeight < 10) {
              newY = originalData.y + originalData.height - 10;
              newHeight = 10;
            }
            break;
          case 'se': // bottom-right
            newWidth = originalData.width + deltaX;
            newHeight = originalData.height + deltaY;
            // Ensure minimum size
            if (newWidth < 10) newWidth = 10;
            if (newHeight < 10) newHeight = 10;
            break;
          case 'sw': // bottom-left
            newX = originalData.x + deltaX;
            newWidth = originalData.width - deltaX;
            newHeight = originalData.height + deltaY;
            // Ensure minimum size
            if (newWidth < 10) {
              newX = originalData.x + originalData.width - 10;
              newWidth = 10;
            }
            if (newHeight < 10) newHeight = 10;
            break;
        }

        // Store the last values
        lastX = newX;
        lastY = newY;
        lastWidth = newWidth;
        lastHeight = newHeight;

        setResizePreview({
          x: newX,
          y: newY,
          width: newWidth,
          height: newHeight,
        });
      };

      let completed = false;
      const captureOptions: AddEventListenerOptions = { capture: true };
      const touchMoveOptions: AddEventListenerOptions = { capture: true, passive: false };

      const handleMouseMove = (e: MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        handleMove(e.clientX, e.clientY);
      };

      const handleTouchMove = (e: TouchEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (e.touches.length === 1) {
          handleMove(e.touches[0].clientX, e.touches[0].clientY);
        }
      };

      const cleanup = () => {
        document.removeEventListener('mousemove', handleMouseMove, captureOptions);
        document.removeEventListener('mouseup', handleEnd, captureOptions);
        window.removeEventListener('mouseup', handleEnd, captureOptions);
        document.removeEventListener('pointerup', handleEnd, captureOptions);
        window.removeEventListener('pointerup', handleEnd, captureOptions);
        document.removeEventListener('pointercancel', handleCancel, captureOptions);
        window.removeEventListener('pointercancel', handleCancel, captureOptions);
        document.removeEventListener('touchmove', handleTouchMove, touchMoveOptions);
        document.removeEventListener('touchend', handleEnd, captureOptions);
        document.removeEventListener('touchcancel', handleCancel, captureOptions);
        window.removeEventListener('keydown', handleKeyDown, captureOptions);
        window.removeEventListener('blur', handleCancel);
      };

      const finishResize = (commit: boolean) => {
        if (completed) return;
        completed = true;
        setIsResizing(false);
        cleanup();

        if (!commit) {
          setResizePreview(null);
          return;
        }

        // Create final update with history if values changed
        if (
          lastX !== originalData.x ||
          lastY !== originalData.y ||
          lastWidth !== originalData.width ||
          lastHeight !== originalData.height
        ) {
          void updateAnnotation({
            id: annotation.id,
            data: {
              data: {
                ...annotation.data,
                x: lastX,
                y: lastY,
                width: lastWidth,
                height: lastHeight,
              },
            },
            skipHistory: false, // Create history entry for the final position
          }).finally(() => setResizePreview(null));
        } else {
          setResizePreview(null);
        }
      };

      function handleEnd() {
        finishResize(true);
      }

      function handleCancel() {
        finishResize(false);
      }

      function handleKeyDown(event: KeyboardEvent) {
        if (event.key === 'Escape') {
          event.preventDefault();
          finishResize(false);
        }
      }

      document.addEventListener('mousemove', handleMouseMove, captureOptions);
      document.addEventListener('mouseup', handleEnd, captureOptions);
      window.addEventListener('mouseup', handleEnd, captureOptions);
      document.addEventListener('pointerup', handleEnd, captureOptions);
      window.addEventListener('pointerup', handleEnd, captureOptions);
      document.addEventListener('pointercancel', handleCancel, captureOptions);
      window.addEventListener('pointercancel', handleCancel, captureOptions);
      document.addEventListener('touchmove', handleTouchMove, touchMoveOptions);
      document.addEventListener('touchend', handleEnd, captureOptions);
      document.addEventListener('touchcancel', handleCancel, captureOptions);
      window.addEventListener('keydown', handleKeyDown, captureOptions);
      window.addEventListener('blur', handleCancel);
    },
    [annotation, updateAnnotation, isInteractive],
  );

  // Handle rotation
  const handleRotationStart = useCallback(
    (e: React.MouseEvent) => {
      if (!isInteractive) return;
      e.stopPropagation();
      e.preventDefault();
      setIsRotating(true);

      // Get the SVG element
      const svg =
        (e.target as SVGElement).ownerSVGElement || (e.target as SVGElement).closest('svg');
      if (!svg) return;

      // Calculate center of rectangle
      const centerX = x + width / 2;
      const centerY = y + height / 2;

      // Get initial angle
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const svgP = pt.matrixTransform(svg.getScreenCTM()!.inverse());
      const initialAngle = Math.atan2(svgP.y - centerY, svgP.x - centerX) * (180 / Math.PI);
      const startRotation = rotationAngle; // Use current rotationAngle, not rotation prop

      // Store the latest rotation value
      let latestRotation = startRotation;

      const handleMove = (clientX: number, clientY: number, shiftKey: boolean = false) => {
        const pt = svg.createSVGPoint();
        pt.x = clientX;
        pt.y = clientY;
        const currentPos = pt.matrixTransform(svg.getScreenCTM()!.inverse());

        // Calculate current angle
        const currentAngle =
          Math.atan2(currentPos.y - centerY, currentPos.x - centerX) * (180 / Math.PI);
        const deltaAngle = currentAngle - initialAngle;
        let newRotation = (startRotation + deltaAngle) % 360;

        // Normalize to 0-360
        if (newRotation < 0) newRotation += 360;

        // Snap to 45-degree increments unless Shift is held
        if (!shiftKey) {
          const snapAngles = [0, 45, 90, 135, 180, 225, 270, 315, 360];
          const snapThreshold = 5; // degrees

          for (const snapAngle of snapAngles) {
            if (Math.abs(newRotation - snapAngle) < snapThreshold) {
              newRotation = snapAngle % 360;
              break;
            }
          }
        }

        latestRotation = newRotation;
        setRotationAngle(newRotation);
      };

      const handleMouseMove = (e: MouseEvent) => {
        e.preventDefault();
        handleMove(e.clientX, e.clientY, e.shiftKey);
      };

      const handleEnd = async () => {
        setIsRotating(false);

        // Final update with history using the latest rotation value
        try {
          await updateAnnotation({
            id: annotation.id,
            data: {
              data: {
                ...annotation.data,
                rotation: latestRotation,
              },
            },
            skipHistory: false,
          });
        } catch (error) {
          logger.error('Failed to save rotation:', error);
        }

        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };

      const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault();
        if (e.touches.length === 1) {
          handleMove(e.touches[0].clientX, e.touches[0].clientY);
        }
      };

      const handleTouchEnd = () => {
        handleEnd();
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
    },
    [
      annotation.id,
      annotation.data,
      updateAnnotation,
      isInteractive,
      x,
      y,
      width,
      height,
      rotationAngle,
    ],
  );

  // Calculate center for rotation
  const centerX = x + width / 2;
  const centerY = y + height / 2;

  return (
    <g
      data-annotation-id={annotation.id}
      className={isInteractive ? 'cursor-pointer' : ''}
      transform={`rotate(${rotationAngle} ${centerX} ${centerY})`}
    >
      {/* Invisible hit area for easier selection - only for stroked rectangles */}
      {(!style.fill || style.fill === 'none' || style.fill === 'transparent') && (
        <rect
          x={x - 8 / scale}
          y={y - 8 / scale}
          width={width + 16 / scale}
          height={height + 16 / scale}
          fill="none"
          stroke="transparent"
          strokeWidth={16 / scale}
          pointerEvents={isInteractive ? 'auto' : 'none'}
          style={{ cursor: isInteractive ? (isRotating ? 'grabbing' : 'pointer') : 'default' }}
          onClick={onClick}
          onMouseDown={isResizing ? undefined : onMouseDown}
          onTouchStart={isResizing ? undefined : onTouchStart}
          onTouchEnd={isResizing ? undefined : onTouchEnd}
          onTouchMove={isResizing ? undefined : onTouchMove}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        />
      )}

      {/* Main rectangle */}
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={style.fill || 'none'}
        fillOpacity={style.fill ? style.opacity || 1 : 0}
        stroke={style.color || '#000000'}
        strokeWidth={strokeWidth}
        strokeOpacity={style.opacity || 1}
        strokeDasharray={style.strokeDasharray}
        pointerEvents={
          style.fill && style.fill !== 'none' && style.fill !== 'transparent' && isInteractive
            ? 'auto'
            : 'none'
        }
        onClick={onClick}
        onMouseDown={isResizing ? undefined : onMouseDown}
        onTouchStart={isResizing ? undefined : onTouchStart}
        onTouchEnd={isResizing ? undefined : onTouchEnd}
        onTouchMove={isResizing ? undefined : onTouchMove}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />

      {/* Hover effect - clean dashed outline */}
      {isHovered && !isSelected && (
        <rect
          x={x - 2 / scale}
          y={y - 2 / scale}
          width={width + 4 / scale}
          height={height + 4 / scale}
          fill="none"
          stroke="#6366f1"
          strokeWidth={1.5 / scale}
          strokeOpacity={0.7}
          strokeDasharray={`${4 / scale} ${2 / scale}`}
          rx={2 / scale}
          pointerEvents="none"
        />
      )}

      {/* Selection/hover indicator */}
      {(isSelected || isHovered) && (
        <>
          {/* Rotation handle when selected */}
          {isSelected && isInteractive && (
            <>
              {/* Rotation handle - positioned above the rectangle */}
              <g>
                {/* Line from center to rotation handle */}
                <line
                  x1={centerX}
                  y1={y - 40 / scale}
                  x2={centerX}
                  y2={y}
                  stroke="#2563eb"
                  strokeWidth={2 / scale}
                  pointerEvents="none"
                />

                {/* Invisible larger hit area for rotation handle */}
                <circle
                  cx={centerX}
                  cy={y - 40 / scale}
                  r={16 / scale}
                  fill="transparent"
                  pointerEvents="all"
                  style={{ cursor: 'grab' }}
                  onMouseDown={handleRotationStart}
                  onTouchStart={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    wrapTouchEvent(handleRotationStart)(e);
                  }}
                />

                {/* Rotation handle button - using foreignObject to match fixtures/LV products */}
                <foreignObject
                  x={centerX - 10 / scale}
                  y={y - 40 / scale - 10 / scale}
                  width={20 / scale}
                  height={20 / scale}
                  style={{ overflow: 'visible' }}
                >
                  <div
                    className="bg-white rounded-full shadow-lg border border-blue-400/80 cursor-grab hover:scale-110 hover:border-blue-500 hover:shadow-xl transition-all flex items-center justify-center"
                    style={{
                      width: '20px',
                      height: '20px',
                    }}
                  >
                    {/* Rotation icon - elegant curved arrow (exact match to fixtures/LV products) */}
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="text-blue-500"
                    >
                      <path
                        d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C15.0902 3 17.7902 4.58073 19.3499 7"
                        stroke="currentColor"
                        strokeWidth={1.8}
                        strokeLinecap="round"
                      />
                      <path
                        d="M17 7L19.5 7L19.5 4.5"
                        stroke="currentColor"
                        strokeWidth={1.8}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </foreignObject>
              </g>

              {/* Angle indicator when rotating */}
              {isRotating && (
                <g>
                  {/* Background for angle text */}
                  <rect
                    x={centerX - 25 / scale}
                    y={centerY - 10 / scale}
                    width={50 / scale}
                    height={20 / scale}
                    rx={4 / scale}
                    fill="rgba(37, 99, 235, 0.9)"
                    pointerEvents="none"
                  />
                  {/* Angle text */}
                  <text
                    x={centerX}
                    y={centerY + 3 / scale}
                    fill="white"
                    fontSize={12 / scale}
                    fontWeight="600"
                    textAnchor="middle"
                    pointerEvents="none"
                  >
                    {Math.round(rotationAngle || rotation)}°
                  </text>
                </g>
              )}
            </>
          )}

          {/* Resize handles when selected */}
          {isSelected && isInteractive && (
            <>
              {[
                { x: x, y: y, handle: 'nw', cursor: 'nwse-resize' },
                { x: x + width, y: y, handle: 'ne', cursor: 'nesw-resize' },
                { x: x + width, y: y + height, handle: 'se', cursor: 'nwse-resize' },
                { x: x, y: y + height, handle: 'sw', cursor: 'nesw-resize' },
              ].map((handle, index) => (
                <g key={index}>
                  {/* Invisible larger hit area for touch */}
                  <rect
                    x={handle.x - 12 / scale}
                    y={handle.y - 12 / scale}
                    width={24 / scale}
                    height={24 / scale}
                    fill="transparent"
                    pointerEvents="all"
                    style={{ cursor: handle.cursor }}
                    onMouseDown={e => handleResizeStart(e, handle.handle)}
                    onTouchStart={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      wrapTouchEvent(e => handleResizeStart(e, handle.handle))(e);
                    }}
                  />
                  {/* Visible handle */}
                  <rect
                    x={handle.x - 6 / scale}
                    y={handle.y - 6 / scale}
                    width={12 / scale}
                    height={12 / scale}
                    fill="#2563eb"
                    stroke="white"
                    strokeWidth={1 / scale}
                    pointerEvents="none"
                  />
                </g>
              ))}
            </>
          )}

          {/* Selection outline */}
          {isSelected && (
            <rect
              x={x - 2 / scale}
              y={y - 2 / scale}
              width={width + 4 / scale}
              height={height + 4 / scale}
              fill="none"
              stroke="#2563eb"
              strokeWidth={2 / scale}
              strokeOpacity={0.5}
              strokeDasharray={`${4 / scale} ${2 / scale}`}
              pointerEvents="none"
            />
          )}
        </>
      )}

      {/* Dimension labels when showDimensions is enabled */}
      {data.showDimensions && hasScale && (
        <>
          {/* Width label - bottom edge */}
          <text
            x={x + width / 2}
            y={y + height + dimensionLabel.offset}
            fill="#3b82f6"
            fontSize={dimensionLabel.fontSize}
            fontWeight="600"
            textAnchor="middle"
            dominantBaseline="hanging"
            pointerEvents="none"
            style={{ userSelect: 'none' }}
          >
            {formatMeasurement(width)}
          </text>
          {/* Height label - right edge */}
          <text
            x={x + width + dimensionLabel.offset}
            y={y + height / 2}
            fill="#3b82f6"
            fontSize={dimensionLabel.fontSize}
            fontWeight="600"
            textAnchor="start"
            dominantBaseline="middle"
            pointerEvents="none"
            style={{ userSelect: 'none' }}
          >
            {formatMeasurement(height)}
          </text>
        </>
      )}
    </g>
  );
}
